import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, CalendarClock, CheckCircle2, Circle, Download, FileText, GraduationCap } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDeadline } from "@/lib/deadline";
/**
 * ApplicationsOverviewPage
 * ------------------------
 * Grid of every shortlisted course, each surfaced as a card showing
 * checklist progress at-a-glance. This is the natural home for the
 * Fast-Apply funnel — students land here to see, in one view, how
 * ready every one of their picks actually is.
 *
 * Data comes from `GET /api/applications` which lazily hydrates one
 * row per shortlisted (user, degree). No new backend endpoint needed.
 */
export default function ApplicationsOverviewPage() {
  const { openSignIn } = useAuth();
  const [rows, setRows] = useState(null);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [signedOut, setSignedOut] = useState(false);
  const [allExported, setAllExported] = useState(false);

  const loadRows = () => api.get("/applications").then(r => {
    setRows(r.data?.applications || []);
    setAllExported(!!r.data?.all_exported);
  });
  // Downloads are plain links, so re-read the list shortly after one starts to pick up the new "Exported" marks.
  const refreshAfterExport = () => setTimeout(() => { loadRows().catch(() => {}); }, 2500);

  useEffect(() => {
    let mounted = true;
    api.get("/applications")
      .then(r => { if (mounted) { setRows(r.data?.applications || []); setAllExported(!!r.data?.all_exported); } })
      .catch(e => {
        if (!mounted) return;
        if (e?.response?.status === 401) setSignedOut(true);
        else setError(e?.response?.data?.detail?.message || "Could not load your applications. Please try again.");
      });
    api.get("/applications/export/preview")
      .then(r => { if (mounted) setPreview(r.data || null); })
      .catch(() => { /* preview is best-effort — buttons just hide */ });
    return () => { mounted = false; };
  }, []);

  if (signedOut) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-8" data-testid="applications-signed-out">
        <div className="mb-6">
          <div className="label-uppercase !text-[10px] text-stone-500">Fast Apply</div>
          <h1 className="font-editorial text-3xl sm:text-4xl leading-tight text-stone-900 mt-1">Your applications</h1>
        </div>
        <div className="mb-6 border border-stone-200 rounded-xl bg-white p-8 text-center">
          <GraduationCap className="w-8 h-8 text-stone-400 mx-auto mb-2" />
          <div className="text-[14px] font-semibold text-stone-900">Sign in to see your applications.</div>
          <div className="text-[13px] text-stone-600 mt-1">Each course on your shortlist becomes an application you can track here.</div>
          <button type="button" onClick={() => openSignIn?.()} data-testid="applications-sign-in"
            className="inline-flex items-center gap-1 mt-4 rounded-full bg-[#002147] px-4 py-2 text-[13px] text-white hover:bg-[#0b2f5c] transition-colors">
            Sign in <ArrowRight className="w-4 h-4" />
          </button>
        </div>
        <AbroadSpoke />
      </div>
    );
  }
  if (error) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-10">
        <p className="text-rose-700 text-sm" data-testid="applications-error">{error}</p>
        <div className="mt-6"><AbroadSpoke /></div>
      </div>
    );
  }
  if (!rows) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-10 text-stone-500 text-sm" data-testid="applications-loading">
        Loading your applications…
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8" data-testid="applications-overview-page">
      <Link
        to="/shortlist"
        data-testid="applications-back-to-shortlist"
        className="inline-flex items-center gap-1 text-[13px] text-stone-500 hover:text-stone-800 mb-3"
      >
        <ArrowLeft className="w-4 h-4" /> Back to shortlist
      </Link>
      <div className="mb-6">
        <div className="label-uppercase !text-[10px] text-stone-500">Fast Apply</div>
        <h1 className="font-editorial text-3xl sm:text-4xl leading-tight text-stone-900 mt-1">
          Your applications
        </h1>
        <p className="text-[13px] text-stone-600 mt-2 max-w-2xl leading-relaxed">
          One card per shortlisted course. Open any card to draft your personal statement,
          invite referees, and track the checklist toward "ready to send".
        </p>
      </div>

      <AbroadSpoke />

      {rows.length === 0 ? (
        <div
          data-testid="applications-empty"
          className="border border-stone-200 rounded-xl bg-white p-8 text-center"
        >
          <GraduationCap className="w-8 h-8 text-stone-400 mx-auto mb-2" />
          <div className="text-[14px] font-semibold text-stone-900">No shortlisted courses yet.</div>
          <div className="text-[13px] text-stone-600 mt-1">
            Add courses to your shortlist from the Explore page and they'll appear here as applications.
          </div>
          <Link
            to="/shortlist"
            data-testid="applications-empty-cta"
            className="inline-flex items-center gap-1 mt-4 text-[13px] text-emerald-700 hover:underline"
          >
            Go to shortlist <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      ) : (
        <>
          <FastApplyExports preview={preview} onExport={refreshAfterExport} />
          <ExportProgress rows={rows} allExported={allExported} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="applications-grid">
            {rows.map((row, i) => (
              <ApplicationCard key={row.degree_id || i} row={row} index={i} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// Spoke into Apply: the country primers feed this page.
function AbroadSpoke() {
  return (
    <Link
      to="/apply"
      data-testid="applications-abroad-spoke"
      className="group mb-6 flex items-center justify-between gap-4 rounded-xl border border-stone-200 bg-[#F3F1EC] px-5 py-4 hover:border-stone-400 transition-colors"
    >
      <div>
        <div className="label-uppercase !text-[10px] text-stone-500">Applying abroad?</div>
        <div className="font-editorial text-lg text-stone-900 mt-0.5">Start with the country, not the course.</div>
        <div className="text-[13px] text-stone-600 mt-1">Platforms, deadlines, documents and visa rules for each country.</div>
      </div>
      <ArrowRight className="w-5 h-5 text-stone-500 shrink-0 transition-transform group-hover:translate-x-1" />
    </Link>
  );
}

// The Apply stop on the journey map is done once every application here has been exported.
function ExportProgress({ rows, allExported }) {
  const done = rows.filter((r) => r.exported).length;
  return (
    <div data-testid="applications-export-progress" data-all-exported={allExported ? "true" : "false"}
      className={`mb-5 flex items-center gap-2 rounded-lg px-4 py-3 text-[13px] ${allExported ? "bg-emerald-600 text-white" : "bg-stone-100 text-stone-700"}`}>
      {allExported ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <Download className="w-4 h-4 shrink-0" />}
      <span>
        {allExported
          ? "Every application is exported. The Apply stop on your journey is complete."
          : <><strong className="font-mono">{done}/{rows.length}</strong> applications exported. Export them all to complete the Apply stop on your journey.</>}
      </span>
    </div>
  );
}

function ApplicationCard({ row, index }) {
  const pct = row.total_items ? Math.round(100 * (row.checked_count || 0) / row.total_items) : 0;
  const ready = row.status === "ready";
  const deadline = useMemo(() => formatDeadline(row.application_deadline), [row.application_deadline]);
  return (
    <Link
      to={`/applications/${row.degree_id}`}
      data-testid={`application-card-${index}`}
      className="group block border border-stone-200 rounded-xl bg-white p-5 hover:border-emerald-500 transition-colors"
    >
      <div className="flex items-start justify-between gap-3 mb-1.5">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold truncate">
            {row.university || "—"}
          </div>
          <h3 className="text-[15px] font-semibold text-stone-900 leading-snug mt-0.5 line-clamp-2">
            {row.degree_title || row.degree_id}
          </h3>
        </div>
        {ready ? (
          <span
            data-testid={`application-card-${index}-status`}
            className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-semibold px-2 py-0.5 flex-none"
          >
            <CheckCircle2 className="w-3 h-3" /> Ready
          </span>
        ) : (
          <span
            data-testid={`application-card-${index}-status`}
            className="inline-flex items-center gap-1 rounded-full bg-stone-100 text-stone-700 text-[10px] font-semibold px-2 py-0.5 flex-none"
          >
            <Circle className="w-3 h-3" /> Draft
          </span>
        )}
      </div>

      {deadline && (
        <div
          data-testid={`application-card-${index}-deadline`}
          className={`mt-1 inline-flex items-center gap-1 text-[11.5px] ${deadline.urgent ? "text-rose-700" : "text-stone-600"}`}
        >
          <CalendarClock className="w-3.5 h-3.5" />
          <span>Deadline: <strong>{deadline.pretty}</strong>{deadline.relative && <> · {deadline.relative}</>}</span>
        </div>
      )}

      <div className="mt-3">
        <div className="flex items-center justify-between text-[11px] text-stone-500 mb-1">
          <span>Checklist</span>
          <span className="font-mono">
            {row.checked_count || 0}/{row.total_items || 6} · {pct}%
          </span>
        </div>
        <div className="h-1.5 rounded-full bg-stone-100 overflow-hidden">
          <div
            data-testid={`application-card-${index}-progress`}
            className={ready ? "h-full bg-emerald-500" : "h-full bg-emerald-400"}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between text-[12px]">
        <span className="text-emerald-700 group-hover:underline inline-flex items-center gap-1">
          <FileText className="w-3.5 h-3.5" /> Open package
        </span>
        {row.exported && (
          <span data-testid={`application-card-${index}-exported`}
            className="ml-auto mr-2 inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-800 text-[10px] font-semibold px-2 py-0.5">
            <Download className="w-3 h-3" /> Exported
          </span>
        )}
        <span className="text-stone-400 group-hover:text-emerald-700">
          <ArrowRight className="w-4 h-4" />
        </span>
      </div>
    </Link>
  );
}


/**
 * FastApplyExports
 * ----------------
 * Two one-tap bulk downloads for the student's shortlist:
 *   • UCAS CSV   — one row per UK course (max 5 UCAS choices)
 *   • Common App — PDF data-pack covering every US course
 *
 * The server returns 404 when there's nothing to export, so we gate
 * each button on the preview counts (null preview → both hidden).
 */
function FastApplyExports({ preview, onExport }) {
  const base = useMemo(() => (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, ""), []);
  if (!preview) return null;
  const ucasCount = preview?.ucas?.count || 0;
  const caCount   = preview?.commonapp?.count || 0;
  if (ucasCount === 0 && caCount === 0) return null;
  const ucasHref = `${base}/api/applications/export/ucas.csv`;
  const caHref   = `${base}/api/applications/export/commonapp.pdf`;
  return (
    <div
      data-testid="fast-apply-exports"
      className="mb-5 border border-emerald-200 bg-emerald-50/60 rounded-xl p-4"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-emerald-800 font-semibold">
            Fast Apply · one-tap exports
          </div>
          <div className="text-[13px] text-stone-700 mt-1 max-w-xl leading-relaxed">
            Leave with the files you need. UCAS and Common App are both web-only —
            these downloads give you a copy-paste-ready pack so you don't have to
            retype your profile into either system.
          </div>
        </div>
        <div className="flex items-center gap-2 flex-none">
          {ucasCount > 0 && (
            <a
              href={ucasHref}
              onClick={onExport}
              data-testid="export-ucas-csv"
              className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 py-2 rounded-lg bg-white border border-emerald-300 text-emerald-800 hover:bg-emerald-100"
            >
              <Download className="w-3.5 h-3.5" />
              UCAS CSV · {ucasCount}{preview?.ucas?.truncated ? "/5" : ""}
            </a>
          )}
          {caCount > 0 && (
            <a
              href={caHref}
              onClick={onExport}
              data-testid="export-commonapp-pdf"
              className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 py-2 rounded-lg bg-white border border-emerald-300 text-emerald-800 hover:bg-emerald-100"
            >
              <Download className="w-3.5 h-3.5" />
              Common App pack · {caCount}
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
